import { memo, useMemo } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'

import { useBackup, useContactData, useReassignment } from '../../../contexts'
import { useContactDeactivation } from '../../../hooks/useContactDeactivation'
import { ContactDeactivationStepProps, ReassignmentItem } from '../../../models'
import { contactDeactivationModalTranslations } from '../../../translation'
import { ContactDeactivationFooter } from '../../contactDeactivationFooter'
import { ContactDeactivationHeader } from '../../contactDeactivationHeader'

import { ReassignedItem } from './components/reassignedItem'

import styles from './CompletionStep.module.scss'

type Props = Pick<ContactDeactivationStepProps, 'onCancel'> & {
  onDeactivate: (deactivatedContact: IContact) => void
}

export const CompletionStep = memo<Props>(({ onCancel, onDeactivate }) => {
  const { contact } = useContactData()
  const { rolesToReassign, flagsToReassign } = useReassignment()
  const { backupedContact } = useBackup()

  const { deactivateContact, loaderState } = useContactDeactivation({
    contact,
    rolesToReassign,
    flagsToReassign,
    onDeactivate,
  })

  const reassignedRoles: ReassignmentItem[] = useMemo(
    () =>
      rolesToReassign.map((role) => ({
        key: role.role,
        displayName: role.role,
        contact: role.contact,
      })),
    [rolesToReassign]
  )

  const reassignedFlags: ReassignmentItem[] = useMemo(
    () =>
      flagsToReassign.map((flag) => ({
        key: flag.flag.code,
        displayName: flag.flag.code,
        contact: flag.contact,
      })),
    [flagsToReassign]
  )

  return (
    <div className={styles.container}>
      <LoaderOverlay isVisible={loaderState.isLoading} message={loaderState.message} />
      <ContactDeactivationHeader
        title={contactDeactivationModalTranslations.completionStep.title}
        description={contactDeactivationModalTranslations.completionStep.description}
        descriptionParams={[{ key: '{email}', value: contact.email }]}
      />
      <div className={styles.content}>
        <RenderWhen condition={rolesToReassign.length > 0}>
          <ReassignedItem
            title={contactDeactivationModalTranslations.completionStep.reassignedRoles}
            items={reassignedRoles}
          />
        </RenderWhen>
        <RenderWhen condition={flagsToReassign.length > 0}>
          <ReassignedItem
            title={contactDeactivationModalTranslations.completionStep.reassignedFlags}
            items={reassignedFlags}
          />
        </RenderWhen>
        <RenderWhen condition={!!backupedContact}>
          <div className={styles.block}>
            <h6>
              <Translation
                text={contactDeactivationModalTranslations.completionStep.backupTitle}
                span
              />
            </h6>
            <p>
              <Translation
                text={contactDeactivationModalTranslations.completionStep.backupDescription}
                replaceParams={[{ key: '{email}', value: backupedContact?.email || '' }]}
                span
              />
            </p>
          </div>
        </RenderWhen>
      </div>
      <ContactDeactivationFooter onCancel={onCancel} onProceed={deactivateContact} />
    </div>
  )
})
